"use client";

import { ComponentPropsWithoutRef, FC, forwardRef, PropsWithChildren, useState } from "react";
import { type AutoPublishState, autoPublishAccounts, useAutoPublish } from "../../hooks/useAutoPublish";
import { type SocialProviderAccount, useSocialProviderAccounts } from "../../hooks/useSocialProviderAccounts";
import { CompactAccountAvatarStack } from "../AccountsSelectDropdown";
import Button from "../Button";
import DropdownMenu from "../DropdownMenu";
import { getProjectCalendarHref } from "../calendar/projectCalendar";
import { AutoPublishPanel, keepOpenOnCalendarClick } from "./AutoPublishControls";
import { AUTO_POST_STATUS, getAutoPublishDisplayStatus, autoPublishBusyLabel, autoPostStateTooltip, autoPublishFirstPost, isAutoPostWaitingForShorts } from "./autoPostStatus";
import { AUTO_POST } from "./autoPostName";
import { useAutoPublishSwitch } from "./useAutoPublishSwitch";
import { autoPostQueueTooltip } from "./autoPostQueueTooltip";
import AutoPostBolt from "./AutoPostBolt";

/**
 * The Auto post half of [Schedule | ⚡ accounts]: the selected destinations
 * follow the glowing bolt while on. Forwards DropdownMenu.Trigger's ref and handlers.
 */
export const AutoPublishTrigger = forwardRef<HTMLButtonElement, {
  on: boolean;
  waitingForShorts?: boolean;
  status?: string | null;
  queue?: Pick<AutoPublishState, "upcoming" | "nextPostAt">;
  accounts: SocialProviderAccount[];
  selectedAccountIds: string[];
} & ComponentPropsWithoutRef<"button">>(
  ({ on, waitingForShorts = false, status, queue, accounts, selectedAccountIds, ...rest }, ref) => {
    const selectedAccounts = on ? accounts.filter((account) => selectedAccountIds.includes(account.id)) : [];
    const expanded = rest["aria-expanded"] === true || rest["aria-expanded"] === "true";
    const queueTooltip = on && queue
      ? autoPostQueueTooltip({ count: queue.upcoming, nextPostAt: queue.nextPostAt })
      : null;
    const ringClassName = "ring-foreground group-hover:ring-foreground-active group-aria-[expanded=true]:ring-foreground-active";

    return (
      <Button
        {...rest}
        ref={ref}
        size="m"
        variant="primary"
        // The divider: a faint line of the label's color on the seam, inset like the label.
        className={`group rounded-l-none border-l-0 before:absolute before:inset-y-2 before:left-0 before:w-px before:bg-current before:opacity-25 before:content-[''] ${selectedAccounts.length > 0 ? "w-auto" : ""}`}
        aria-label={queueTooltip?.label ?? (on ? `${AUTO_POST}, on` : AUTO_POST)}
        data-tooltip-id="global"
        data-tooltip-hidden={expanded}
        data-tooltip-content={expanded || queueTooltip ? undefined : status ?? undefined}
        data-tooltip-html={expanded ? undefined : queueTooltip?.html ?? (status ? undefined : autoPostStateTooltip(on))}
      >
        <AutoPostBolt active={on} waiting={waitingForShorts} />
        {selectedAccounts.length > 0 ? (
          <Button.Content>
            <CompactAccountAvatarStack
              accounts={accounts}
              selectedAccountIds={selectedAccountIds}
              maxAvatars={2}
              ringClassName={ringClassName}
            />
          </Button.Content>
        ) : null}
      </Button>
    );
  },
);
AutoPublishTrigger.displayName = "AutoPublishTrigger";

/**
 * Fuses the Schedule button (first child) and the bolt into one split button,
 * with no borders on the seam: a hovered half's fill meets the divider.
 * A disabled Schedule button fades, which already marks the seam: no divider then.
 */
export const AutoPublishSplit: FC<PropsWithChildren> = ({ children }) => (
  <div className="flex min-w-0 [&>*:first-child]:min-w-0 [&>*:first-child]:flex-1 [&>*:first-child]:rounded-r-none [&>*:first-child]:border-r-0 [&>*:disabled+*]:before:hidden">
    {children}
  </div>
);

/**
 * The results header's Schedule button (`children`) with Auto publish as its
 * bolt half, opening the project's switch, accounts and hours. Just the
 * Schedule button until the project's settings load, or on someone else's.
 */
const AutoPublishButton: FC<PropsWithChildren<{ folderId: string; taskStatus?: string; shortCount?: number }>> = ({
  folderId,
  taskStatus,
  shortCount,
  children,
}) => {
  const { state, statusState, save, isLoading, isSaving, isStarting } = useAutoPublish(folderId, { refreshKey: taskStatus });
  const accountsQuery = useSocialProviderAccounts().readAccounts;
  const rawAccounts = accountsQuery.data ?? [];
  const accounts = autoPublishAccounts(rawAccounts);
  const [open, setOpen] = useState(false);
  const { onChange, openConnect, reconnectAccount, layer, checking, prefetch, shownState } = useAutoPublishSwitch({
    surface: "results",
    state,
    accounts,
    apply: save,
    onDialog: () => setOpen(false),
    onReturn: () => setOpen(true),
    open,
  });
  if (!state && !isLoading) return <>{children}</>;
  const loading = isLoading || accountsQuery.isLoading;
  const busy = Boolean(loading || isSaving || checking);
  const busyLabel = loading ? AUTO_POST_STATUS.loading : autoPublishBusyLabel({ isSaving, isStarting });
  const status = getAutoPublishDisplayStatus({ state: loading ? null : statusState, accounts, taskStatus, shortCount, isStarting });
  const queue = !loading && !isStarting && statusState?.enabled ? statusState : undefined;
  const waitingForShorts = isAutoPostWaitingForShorts({ enabled: queue?.enabled, taskStatus, upcoming: queue?.upcoming });
  const firstPostEstimate = waitingForShorts && queue ? autoPublishFirstPost(queue, accounts) : null;

  return (
    <>
      <AutoPublishSplit>
        {children}
        <DropdownMenu size="l" positions={["bottom", "top"]} align="end" open={open} onOpenChange={setOpen}
          onClickOutside={keepOpenOnCalendarClick}>
          <DropdownMenu.Trigger>
            <AutoPublishTrigger on={state?.enabled ?? false} waitingForShorts={waitingForShorts} status={status} queue={queue} aria-busy={busy || undefined} accounts={accounts} selectedAccountIds={state?.accountIds ?? []} />
          </DropdownMenu.Trigger>
          <DropdownMenu.Content disablePadding className="w-80 max-w-[calc(100vw-1rem)]">
            <fieldset disabled={busy} className="min-w-0">
              <AutoPublishPanel state={loading ? null : shownState} accounts={accounts} rawAccounts={rawAccounts}
                disabled={busy} busyLabel={busyLabel} status={status} queue={queue} shortCount={shortCount} checking={checking}
                waitingForShorts={waitingForShorts} firstPostEstimate={firstPostEstimate}
                calendarHref={getProjectCalendarHref(folderId, statusState?.nextPostAt)} onShowInCalendar={() => setOpen(false)}
                onSwitchIntent={prefetch} onChange={change => { if (!busy) onChange(change); }}
                onConnect={() => { if (!busy) openConnect(); }}
                onReconnect={account => { if (!busy) reconnectAccount(account); }} />
            </fieldset>
          </DropdownMenu.Content>
        </DropdownMenu>
      </AutoPublishSplit>
      {layer}
    </>
  );
};

export default AutoPublishButton;
